import React, { useEffect, useState } from 'react'
//components
import ExampleContainer from './ExampleContainer'
import {
  Alert,
  BodyText,
  CaptionText,
  PAYPAL_THEME,
  Button,
  ChevronDownIcon,
  Card,
  Link,
  ChevronUpIcon,
} from '@paypalcorp/pp-react'
import Loading from './AiLoading'
import AiGeneragedPill from './AiGeneratedPill'
import Feedback from './Feedback'
import Sparkle from '../../../icons/sparkle'
//syles
import styles from './AiSearch.module.css'
//config
import { faqData } from './config'
import WasThisHelpful from './WasThisHelpful'

/* Note: As the react mardown package is ESM only. using require to import the module 
https://github.com/remarkjs/react-markdown#install
*/
const Markdown = require('react-markdown').default

const { sysColorBackgroundMediumContrast, sysColorAccent4Main } = PAYPAL_THEME

interface AiAnswerSummaryProps {
  handleSuggestionClick: (text: string) => void
  fetchResults: (text: string, uuid: string) => void
  isLoadingSummary: boolean
  answerSummary: string
  errorStatus: string
  setSearchQuery: (text: string) => void
  searchQuery: string
  answerId: string
  searchID: string
  isStreamingComplete: boolean
}
const AiAnswerSummary = ({
  handleSuggestionClick,
  fetchResults,
  isLoadingSummary,
  answerSummary,
  errorStatus,
  setSearchQuery,
  answerId,
  searchQuery,
  searchID,
  isStreamingComplete,
}: AiAnswerSummaryProps) => {
  const fptiData = typeof window !== `undefined` && window.fpti

  const [seeMore, setSeeMore] = useState(true)
  const [isDislikeSelected, setIsDislikeSelected] = useState(false)
  const [isLikeSelected, setIsLikeSelected] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [feedbackSent, setFeedbackSent] = useState(false)

  useEffect(() => {
    // resetting feedback state for a new search
    if (isLoadingSummary) {
      setIsDislikeSelected(false)
      setIsLikeSelected(false)
      setIsFeedbackOpen(false)
      setFeedbackSent(false)
    }
  }, [isLoadingSummary])

  const fetchAnswer = (text: string) => {
    handleSuggestionClick(text)
    fetchResults(text)
    setSearchQuery(text)
  }
  const closeDialog = () => {
    setIsFeedbackOpen(false)
  }

  const ConversationCard = ({ children }) => (
    <div className={styles.answerContainer}>
      <Card className={styles.aiSummaryContainer}>{children}</Card>
    </div>
  )
  const onSeeMoreClick = () => {
    const seeMoreToggle = seeMore ? 'show' : 'hide'
    if (fptiData) {
      const options = {
        data: {
          page: fptiData.page,
          pgrp: fptiData.pgrp,
          tag: `see more-${seeMoreToggle}`,
          link: `see more-${seeMoreToggle}`,
          value: seeMoreToggle,
          component: 'devdiscoverynodeweb',
          answerId,
          name: 'gen_ai_summary',
          searchID,
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.recordClick(options)
      }
    }
  }

  // On click handler for link in AI answer
  const answerLinkClick = (linkProps) => {
    if (fptiData) {
      const options = {
        data: {
          page: fptiData.page,
          pgrp: fptiData.pgrp,
          name: 'gen_ai_summary',
          tag: 'ai-summary-link-clicked',
          link_name: 'ai-summary-link-clicked',
          link: linkProps.href,
          answerId,
          component: 'devdiscoverynodeweb',
          firstPageResult: true,
          searchID,
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.recordClick(options)
      }
    }
  }

  const buttonText = seeMore ? (
    <span className={styles.seeMoreButton} onClick={onSeeMoreClick}>
      <span className={styles.pr05}>See more</span>{' '}
      <ChevronDownIcon size="xs" />
    </span>
  ) : (
    <span className={styles.seeMoreButton} onClick={onSeeMoreClick}>
      <span className={styles.pr05}>Hide</span> <ChevronUpIcon size="xs" />
    </span>
  )
  const renderMarkDown = () => {
    return (
      <Markdown
        components={{
          a: ({ href, ...linkProps }) => (
            <Link
              onClick={() => answerLinkClick(linkProps)}
              href={`${href}?gen_ai_summary=true&search_id=${searchID}`}
              {...linkProps}
              target="_blank"
            />
          ),
        }}
      >
        {answerSummary}
      </Markdown>
    )
  }
  return (
    <>
      {errorStatus && (
        <ConversationCard>
          <BodyText data-testid="hc-gen-ai-error-text">
            To get Quick help, try your search again or skip to the search
            results below.
          </BodyText>
        </ConversationCard>
      )}
      {!isLoadingSummary && !answerSummary && !errorStatus && (
        <ExampleContainer
          sectionData={faqData}
          handleClick={fetchAnswer}
          searchID={searchID}
        />
      )}
      {!errorStatus && (answerSummary || isLoadingSummary) && (
        <ConversationCard>
          <>
            {isStreamingComplete && answerSummary ? (
              <CaptionText
                className={styles.listTitle}
                style={{ color: sysColorBackgroundMediumContrast }}
              >
                QUICK ANSWER
                <AiGeneragedPill />
              </CaptionText>
            ) : (
              <CaptionText
                style={{ color: sysColorAccent4Main }}
                className={styles.loadingAnswerTitle}
              >
                <Sparkle />
                <span className={styles.ml1}>Generating quick answer...</span>
              </CaptionText>
            )}
            {!isLoadingSummary && answerSummary && (
              <>
                <BodyText
                  className={`${styles.answerSummary} ${
                    !seeMore ? undefined : styles.clampContainer
                  }`}
                >
                  {renderMarkDown()}
                </BodyText>
                <div className={styles.answerFooterContainer}>
                  <div className={styles.answerFooter}>
                    <Button
                      tertiary
                      disabled={!isStreamingComplete}
                      onClick={() => setSeeMore(!seeMore)}
                    >
                      {buttonText}
                    </Button>
                    {isStreamingComplete && (
                      <WasThisHelpful
                        searchQuery={searchQuery}
                        answerId={answerId}
                        searchID={searchID}
                        setIsFeedbackOpen={setIsFeedbackOpen}
                        setIsDislikeSelected={setIsDislikeSelected}
                        setIsLikeSelected={setIsLikeSelected}
                        isLikeSelected={isLikeSelected}
                        isDislikeSelected={isDislikeSelected}
                        /** To remove the unselected button*/
                        hideLike={isDislikeSelected}
                        hideDislike={isLikeSelected}
                        /** To disable the feedback button once it is selected */
                        disableLike={isLikeSelected}
                        disableDisLike={isDislikeSelected}
                      />
                    )}
                  </div>
                  <>
                    {isFeedbackOpen && (
                      <Feedback
                        answerId={answerId}
                        searchID={searchID}
                        closeDialog={closeDialog}
                        setIsFeedbackOpen={setIsFeedbackOpen}
                        setFeedbackSent={setFeedbackSent}
                      />
                    )}
                    {feedbackSent && (
                      <Alert
                        type="success"
                        closeButton={{
                          'aria-label': 'dismiss alert',
                          onClick: () => {
                            setFeedbackSent(false)
                          },
                        }}
                      >
                        Feedback sent
                      </Alert>
                    )}
                  </>
                </div>
              </>
            )}
            {isLoadingSummary && <Loading classes={'bgPurple'} />}
          </>
        </ConversationCard>
      )}
    </>
  )
}

export default AiAnswerSummary
